import { useCallback, useContext, useEffect, useRef } from 'react';

import { isNotDefined, type Noop, parseJSON } from '@enterprise/core';

import type { SseEventMap } from '#context/sse/types/sse-event-map.type';
import type { SseHandler } from '#context/sse/types/sse-handler.type';
import type { SsePayloadHandler } from '#context/sse/types/sse-payload-handler.type';

import { SseContext } from '#context/sse/sse.context';

/**
 * React hook for subscribing to Server-Sent Events (SSE) using a context-based approach.
 *
 * Provides methods to register event handlers for SSE events, including:
 * - `on`: Subscribe to a specific event with a handler.
 * - `once`: Subscribe to a specific event with a handler that is automatically removed after the first call.
 * - `onMany`: Subscribe to multiple events at once with their respective handlers.
 *
 * All subscriptions are automatically cleaned up when the component unmounts.
 *
 * @template TEventMap The event map type describing available SSE events and their payloads.
 * @throws {Error} If used outside of an `SseProvider`.
 *
 * @returns {{
 *   on: <TKey extends keyof TEventMap>(event: TKey, handler: SsePayloadHandler<TEventMap[TKey]>) => () => void;
 *   once: <TKey extends keyof TEventMap>(event: TKey, handler: SsePayloadHandler<TEventMap[TKey]>) => () => void;
 *   onMany: (handlers: { [K in keyof TEventMap]?: SsePayloadHandler<TEventMap[K]> }) => () => void;
 * }} An object containing the SSE subscription methods.
 */
export const useSse = <TEventMap extends SseEventMap = SseEventMap>() => {
  const store = useContext(SseContext);

  if (isNotDefined(store)) {
    throw new Error('useSse must be used within an SseProvider');
  }

  const removalsRef = useRef<(() => void)[]>([]);

  const on = useCallback(
    <TKey extends keyof TEventMap>(event: TKey, handler: SsePayloadHandler<TEventMap[TKey]>) => {
      const wrapped: SseHandler = (e: MessageEvent<string>) => {
        const result = parseJSON<TEventMap[TKey]>(e.data);

        if (result.isFailure()) {
          return;
        }

        handler(result.unwrap());
      };

      const eventName = event as string;

      store.listeners[eventName] ??= new Set();
      store.listeners[eventName].add(wrapped);

      store.addEventListener?.(eventName);

      const unsubscribe = () => {
        store.listeners[eventName]?.delete(wrapped);
      };

      removalsRef.current.push(unsubscribe);

      return unsubscribe;
    },
    [store]
  );

  const once = useCallback(
    <TKey extends keyof TEventMap>(event: TKey, handler: SsePayloadHandler<TEventMap[TKey]>) => {
      const wrapped: SsePayloadHandler<TEventMap[TKey]> = (e) => {
        unsubscribe();
        handler(e);
      };

      const unsubscribe = on(event, wrapped);

      return unsubscribe;
    },
    [on]
  );

  const onMany = useCallback(
    (handlers: { [K in keyof TEventMap]?: SsePayloadHandler<TEventMap[K]> }) => {
      const removals: Noop[] = [];

      for (const [event, handler] of Object.entries(handlers)) {
        if (handler) {
          removals.push(on(event as keyof TEventMap, handler as SsePayloadHandler<TEventMap[keyof TEventMap]>));
        }
      }

      return () => {
        for (const removal of removals) {
          removal();
        }
      };
    },
    [on]
  );

  useEffect(() => {
    return () => {
      for (const unsubscribe of removalsRef.current) {
        unsubscribe();
      }

      removalsRef.current = [];
    };
  }, []);

  return { on, once, onMany };
};
